class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.renderSkuMatches(targetElement);

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  // LFH: SKU/Artikelnummer-søgning i dropdown'en. Predictive search kan ikke læse
  // tags/SKU, så vi kalder den tag-bevidste klassiske søgning via en JSON-visning
  // (templates/search.skusuggest.liquid) og skyder trefferne ind øverst i resultatet.
  // Hvis predictive ikke selv fandt produkter, skjuler vi dens "ingen resultater"-besked.
  renderSkuMatches(targetElement) {
    const q = this.getQuery();
    if (!q || q.length < 3) return;
    // Kør kun opslaget når søgeteksten ligner et varenummer (ét alfanumerisk token med
    // mindst ét ciffer). Så sender vi ikke almindelige ordsøgninger til den klassiske
    // søgning og undgår at spamme søge-analytics ("searches with no results" m.m.).
    const looksLikeSku = /^[a-z0-9._\/-]{3,}$/i.test(q) && /\d/.test(q);
    if (!looksLikeSku) return;
    const searchRoute =
      (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.search_url) || '/search';
    const url = `${window.shopUrl}${searchRoute}?type=product&view=skusuggest&q=${encodeURIComponent(q)}*`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => {
        if (this.getQuery() !== q) return;
        if (!Array.isArray(items) || items.length === 0) return;
        const norm = (u) => (u || '').split('?')[0].replace(/\/$/, '');
        const existing = new Set(
          Array.from(targetElement.querySelectorAll('a[href*="/products/"]')).map((a) =>
            norm(a.getAttribute('href'))
          )
        );
        const hadProductResults = existing.size > 0;
        const fresh = items.filter((it) => it && it.url && !existing.has(norm(it.url)));
        if (fresh.length === 0) return;
        const rows = fresh
          .map(
            (it) =>
              `<li><a href="${it.url}" style="display:flex;align-items:center;gap:12px;padding:8px 4px;text-decoration:none;color:inherit;">${
                it.image
                  ? `<img src="${it.image}" alt="" width="48" height="48" style="width:48px;height:48px;object-fit:cover;border-radius:6px;flex:0 0 auto;">`
                  : ''
              }<span style="display:flex;flex-direction:column;line-height:1.3;min-width:0;"><span style="font-weight:600;">${
                it.title
              }</span><span style="font-size:12px;opacity:.7;">${(it.sku || '')}${
                it.price ? ' · ' + it.price : ''
              }</span></span></a></li>`
          )
          .join('');
        const block = document.createElement('div');
        block.className = 'sku-suggest';
        block.style.cssText = 'padding:8px 12px;';
        block.innerHTML = `<p style="margin:0 0 4px;font-size:12px;letter-spacing:.04em;text-transform:uppercase;opacity:.6;">Artikelnummer</p><ul style="list-style:none;margin:0;padding:0;">${rows}</ul>`;
        targetElement.insertAdjacentElement('afterbegin', block);
        // Skjul predictive's "keine Ergebnisse"-besked, hvis den ikke selv fandt produkter.
        if (!hadProductResults) {
          Array.from(targetElement.children).forEach((child) => {
            if (child !== block) child.style.display = 'none';
          });
        }
      })
      .catch(() => {});
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);